import { useEffect } from 'react';

/**
 * 事件监听器配置接口
 */
interface EventListenerConfig {
  /** 事件类型 */
  type: string;
  /** 事件处理器 */
  handler: EventListener;
  /** 事件监听器选项，默认为 { passive: false } */
  options?: AddEventListenerOptions;
}

/**
 * 自定义 Hook：为指定元素添加非 passive 模式的事件监听器
 * 主要用于解决触摸事件中 preventDefault() 无法生效的问题
 *
 * @param ref - 目标元素的 ref
 * @param events - 事件监听器配置数组
 *
 * @example
 * ```tsx
 * const elementRef = useRef<HTMLDivElement>(null);
 *
 * useNonPassiveEventListener(elementRef, [
 *   {
 *     type: 'touchmove',
 *     handler: (e) => {
 *       e.preventDefault(); // 现在可以正常工作
 *     }
 *   },
 *   {
 *     type: 'touchend',
 *     handler: handleTouchEnd,
 *     options: { passive: false, capture: true }
 *   }
 * ]);
 * ```
 */
function useNonPassiveEventListener(
  ref: React.RefObject<HTMLElement> | React.MutableRefObject<HTMLElement | undefined>,
  events: EventListenerConfig[]
) {
  useEffect(() => {
    const element = ref.current;
    if (!element || !events.length) {
      return;
    }

    // 添加所有事件监听器
    events.forEach(({ type, handler, options = { passive: false } }) => {
      element.addEventListener(type, handler, options);
    });

    // 清理函数：移除所有事件监听器
    return () => {
      events.forEach(({ type, handler }) => {
        element.removeEventListener(type, handler);
      });
    };
  }, [ref, events]);
}

export default useNonPassiveEventListener;
